import React, { useCallback, useEffect, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { ITag, TeachingBubble, ITeachingBubbleStyles, DirectionalHint } from "office-ui-fabric-react";
import {
    makeStyles,
    mergeClasses,
    tokens,
    Button,
    Field,
    Input,
    Link,
    Text,
    ProgressBar,
    FluentProvider,
} from "@fluentui/react-components";
import {
    ArrowClockwise16Regular,
    Dismiss16Regular,
    Folder16Regular,
    Info20Filled,
    Search20Regular,
} from "@fluentui/react-icons";
import { Log, Logger } from "../../services/Logger";
import { getEnglishTranslation } from "../../i18n/getEnglishTranslation";
import { EmailApiService } from "../../services/NewformaApi/EmailApiService";
import { EmailListItem } from "../../models/EmailListResponse";
import { MsGraphApiService, waitForNewformaFolderId } from "../../services/MsGraphApiService";
import { OfficeNotificationService } from "../../services/officeUi/OfficeNotificationService";
import { AnalyticsActionType, AnalyticsCategoryType, AnalyticsManager } from "../../services/AnalyticsManager";
import { IProjectsService } from "../../services/NewformaApi/IProjectsService";
import { NrnServiceWrapper } from "../../services/NrnServiceWrapper";
import { AuthApiService } from "../../services/NewformaApi/AuthApiService";
import { ProjectsCacheKeys } from "../../models/StorageKeys";
import { InvalidateCacheService } from "../../services/NewformaApi/InvalidateCacheService";
import { MessageTypes, Messenger } from "../../services/Messenger";
import { OfficeRoamingSettings } from "../../services/OfficeRoamingSettings";
import { Paging } from "../../models/ProjectKeywordsResponse";
import { AppPage } from "../shared/navigationHeader/NavigationHeaderComponent";
import { ExpiredSessionError } from "../../models/ExpiredSessionError";
import { SmartFilingManagerFluent9 } from "../../services/SmartFiling/SmartFilingManagerFluent9";
import { MailboxItem } from "../../services/OfficeWrapper";
import { NewformaToastIntent } from "../_base/Toast";
import { FilteredProjectPickerFluent9 } from "../shared/filteredProjectPicker/FilteredProjectPickerFluent9";
import { PaginatedProjectPickerFluent9 } from "../shared/paginatedProjectPicker/PaginatedProjectPickerFluent9";
import { IProjectData } from "../../models/shared/IProjectData";
import { Project } from "../../models/ProjectsResponse";
import EmailListFluent9 from "./emailList/EmailListFluent9";
import { NewformaSwitch } from "../_base/NewformaSwitch";
import { NewformaFolderTreeNode } from "../_base/NewformaFolderTree";
import SubfolderPickerFluent9 from "../shared/subfolderPicker/SubfolderPickerFluent9";
import { FOLDER_FETCH_PAGE_MAX_SIZE } from "../../constants/projectFiles";
import { buildEmailFolderNrn } from "../../helpers/NrnHelpers";
import { ProjectHelper } from "../../helpers/ProjectHelper";
import { ProjectSettingsResponse } from "../../models/projects/ProjectSettingsResponse";
import { useTheme } from "../../hooks/useTheme";

// Approximate rendered height of a single email row (see EmailListFluent9 `emailRow`:
// 12px+12px padding + ~60px of three text lines + 1px border). Used to guarantee the
// email list never collapses below a usable size before the page shows a scrollbar.
const EMAIL_ROW_APPROX_HEIGHT = 85;
const MIN_VISIBLE_EMAIL_ROWS = 2;
const EMAIL_LIST_MIN_HEIGHT = EMAIL_ROW_APPROX_HEIGHT * MIN_VISIBLE_EMAIL_ROWS;
const SUBFOLDER_SORT_BY = "asc";

// Exported so tests can exercise the real Griffel style generation (the component spec mocks makeStyles).
export const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "4px 20px 0 20px",
        gap: 0,
        // Scroll the whole add-in only when its content no longer fits (e.g. short window
        // or zoomed in). `auto` keeps the scrollbar hidden whenever everything fits.
        overflowY: "auto",
        userSelect: "none",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    projectSection: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalXS,
        paddingLeft: tokens.spacingHorizontalXS,
        paddingRight: tokens.spacingHorizontalXS,
        marginBottom: "8px",
    },
    searchSection: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        paddingLeft: tokens.spacingHorizontalXS,
        paddingRight: tokens.spacingHorizontalXS,
        marginBottom: "8px",
    },
    searchField: {
        ":global(.fui-Field__label)": {
            marginBottom: tokens.spacingVerticalXS,
        },
    },
    searchInput: {
        width: "100%",
        minHeight: "32px",
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderBottomColor: tokens.colorNeutralStrokeAccessible,
        borderRadius: "4px",
        boxShadow: "none",
        fontSize: "14px",
        color: tokens.colorNeutralForeground1,
        "&&&:hover": {
            border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
            borderBottomColor: tokens.colorNeutralStrokeAccessible,
        },
        ":focus-within": {
            border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
            borderBottomColor: tokens.colorCompoundBrandStroke,
            boxShadow: "none",
        },
        ":focus-within::after": {
            borderBottom: `2px solid ${tokens.colorCompoundBrandStroke}`,
        },
        ":focus-within:active::after": {
            borderBottomColor: tokens.colorCompoundBrandStroke,
        },
    },
    searchInputDark: {
        "&&&:not(:hover):not(:focus-within)": {
            "border-color": "transparent",
            "border-bottom-color": "transparent",
        },
    },
    searchClearButton: {
        minWidth: "24px",
        width: "24px",
        height: "24px",
        color: tokens.colorNeutralForeground3,
    },
    actionLinkRow: {
        display: "flex",
        justifyContent: "flex-end",
    },
    actionLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "12px",
        lineHeight: "16px",
        "& svg": {
            fontSize: "13px",
        },
    },
    listHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: tokens.spacingHorizontalXS,
        paddingRight: tokens.spacingHorizontalXS,
        marginBottom: "4px",
    },
    selectAllLink: {
        fontSize: "12px",
        lineHeight: "16px",
    },
    folderLabel: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
    },
    emailListContainer: {
        flex: 1,
        // Keep at least two email rows visible; below this the page scrolls instead of
        // squeezing the list down to nothing.
        minHeight: `${EMAIL_LIST_MIN_HEIGHT}px`,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        paddingTop: 0,
        marginBottom: 0,
        color: tokens.colorNeutralForeground3,
        scrollbarColor: `${tokens.colorNeutralStrokeAccessible} ${tokens.colorNeutralBackground1}`,
        "&::-webkit-scrollbar": {
            width: "14px",
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: tokens.colorNeutralBackground1,
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: tokens.colorNeutralStrokeAccessible,
            border: `3px solid ${tokens.colorNeutralBackground1}`,
            borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: tokens.colorNeutralForeground3,
        },
        "&::-webkit-scrollbar-button:single-button": {
            display: "block",
            width: "14px",
            height: "14px",
            backgroundColor: tokens.colorNeutralBackground1,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
        },
        "&::-webkit-scrollbar-button:single-button:vertical:decrement": {
            backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath fill='currentColor' d='M7 4l4 5H3z'/%3E%3C/svg%3E\")",
        },
        "&::-webkit-scrollbar-button:single-button:vertical:increment": {
            backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath fill='currentColor' d='M3 5h8l-4 5z'/%3E%3C/svg%3E\")",
        },
    },
    footer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "stretch",
        flexShrink: 0,
        position: "sticky",
        bottom: 0,
        zIndex: 1,
        paddingTop: 0,
        paddingLeft: "24px",
        paddingRight: "24px",
        marginLeft: "-20px",
        marginRight: "-20px",
        marginTop: 0,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    submitValidationLabel: {
        width: "100%",
        minHeight: "28px",
        display: "flex",
        alignItems: "flex-end",
        fontSize: "12px",
        lineHeight: "14px",
        color: tokens.colorNeutralForeground1,
        paddingBottom: "2px",
        boxSizing: "border-box",
    },
    submitValidationAsterisk: {
        color: tokens.colorPaletteRedForeground1,
    },
    submitProgress: {
        width: "100%",
        height: "36px",
        minHeight: "36px",
        padding: "4px 8px",
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: "4px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "6px",
    },
    submitProgressLight: {
        backgroundColor: tokens.colorNeutralBackgroundDisabled,
    },
    submitProgressContent: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        gap: "4px",
        minWidth: 0,
    },
    submitProgressText: {
        display: "block",
        marginBottom: 0,
        fontSize: "12px",
        lineHeight: "14px",
        color: tokens.colorNeutralForeground1,
    },
    submitProgressBar: {
        borderRadius: "4px",
    },
    submitButtonContainer: {
        width: "100%",
        height: "64px",
        minHeight: "64px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
    },
    submitButton: {
        width: "100%",
        minWidth: 0,
        height: "32px",
        minHeight: "32px",
        borderRadius: "4px",
        backgroundColor: tokens.colorBrandBackground,
        border: `none`,
        color: tokens.colorNeutralForegroundOnBrand,
        fontWeight: tokens.fontWeightSemibold,
        ":hover": {
            backgroundColor: tokens.colorBrandBackgroundHover,
            border: `none`,
            color: tokens.colorNeutralForegroundOnBrand,
        },
        ":active": {
            backgroundColor: tokens.colorBrandBackgroundPressed,
            border: `none`,
            color: tokens.colorNeutralForegroundOnBrand,
        },
        ":disabled": {
            backgroundColor: tokens.colorNeutralBackgroundDisabled,
            border: `none`,
            color: tokens.colorNeutralForegroundDisabled,
            cursor: "default",
            boxShadow: "none",
            outline: "none",
        },
        "&&&:disabled:hover": {
            backgroundColor: tokens.colorNeutralBackgroundDisabled,
            border: `none`,
            color: tokens.colorNeutralForegroundDisabled,
            cursor: "default",
            boxShadow: "none",
            outline: "none",
        },
        "&&&:disabled:active": {
            backgroundColor: tokens.colorNeutralBackgroundDisabled,
            border: `none`,
            color: tokens.colorNeutralForegroundDisabled,
            cursor: "default",
            boxShadow: "none",
            outline: "none",
        },
    },
    submitButtonDark: {
        "&&&:not(:hover)": {
            border: `none`,
            boxShadow: "none",
            outline: "none",
        },
    },
    teachingBubbleBoldText: {
        fontWeight: tokens.fontWeightSemibold,
    },
    loadingContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        userSelect: "none",
    },
    subfolderPickerContainer: {
        width: "100%",
        marginTop: tokens.spacingVerticalXS,
    },
    searchInputField: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontSize: "14px",
        color: tokens.colorNeutralForeground1,
        "::placeholder": {
            color: tokens.colorNeutralForeground4,
        },
    },
});

interface FileMultipleEmailFluent9Props extends LocalizeContextProps {
    logger: Logger;
    onExpiredSession: () => void;
    onShowToast: (message: string | null, type: NewformaToastIntent) => void;
    onSetNavigationPage: (page: AppPage) => void;
    mailboxItem: MailboxItem | null;
    smartFilingManager: SmartFilingManagerFluent9;
    emailApiService: EmailApiService;
    deleteEmailAfterFiling: boolean;
    msGraphApiService: MsGraphApiService;
    officeNotificationService: OfficeNotificationService;
    analyticsManager: AnalyticsManager;
    projectsService: IProjectsService;
    invalidateCacheService: InvalidateCacheService;
    nrnServiceWrapper: NrnServiceWrapper;
    authApiService: AuthApiService;
    shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache: boolean;
    messenger: Messenger;
    officeRoamingSettings: OfficeRoamingSettings;
    isGlobalProjectSearchSupported: boolean;
    isFileEmailIntoSubfolderSupported: boolean;
    onDismissTeachingBubble: () => void;
    showNoProjectsToast: () => void;
    hideNoProjectsToast: () => void;
    culture: string;
}

const getNextSelectedEmailIds = (prevSelectedEmailIds: Set<string>, nextSelectedEmailIds: Set<string>): Set<string> => {
    if (prevSelectedEmailIds.size !== nextSelectedEmailIds.size) {
        return nextSelectedEmailIds;
    }

    for (const emailId of Array.from(nextSelectedEmailIds)) {
        if (!prevSelectedEmailIds.has(emailId)) {
            return nextSelectedEmailIds;
        }
    }

    return prevSelectedEmailIds;
};

const getErrorResponseBody = (error: any): any => {
    if (error?.responseJSON) {
        return error.responseJSON;
    }

    if (!error?.responseText) {
        return undefined;
    }

    try {
        return JSON.parse(error.responseText.replace(/\n/g, ""));
    } catch {
        return undefined;
    }
};

const isExpiredSessionOrTokenError = (error: any): boolean => {
    if (ExpiredSessionError.isInstanceOf(error)) {
        return true;
    }

    const responseBody = getErrorResponseBody(error);
    const errorCode = responseBody?.error?.code || responseBody?.code || responseBody?.type || "";
    const errorMessage = responseBody?.error?.message || responseBody?.message || error?.message || "";

    return (
        error?.status === 401 ||
        error?.statusCode === 401 ||
        errorCode === "InvalidAuthenticationToken" ||
        errorCode.indexOf("expired_token") >= 0 ||
        errorMessage.toLowerCase().indexOf("access token has expired") >= 0
    );
};

const mapProjects = (projects: Project[] = []): IProjectData[] =>
    projects.map((project) => ({
        nrn: project.nrn,
        name: project.name,
        displayName: project.number ? `${project.number} - ${project.name}` : project.name,
        number: project.number,
        code: project.code,
        nixEnabled: project.nixEnabled,
        autoFileMailboxFolderName: project.autoFileMailboxFolderName,
    }));

const FileMultipleEmailFluent9Inner: React.FC<FileMultipleEmailFluent9Props> = (props) => {
    const styles = useStyles();
    const { theme, isDarkMode } = useTheme();
    const [isLoadingProjects, setIsLoadingProjects] = useState(true);
    const [isRefreshingProjects, setIsRefreshingProjects] = useState(false);
    const [isLoadingEmails, setIsLoadingEmails] = useState(false);
    const [isLoadingMoreEmails, setIsLoadingMoreEmails] = useState(false);
    const [isReloadingEmails, setIsReloadingEmails] = useState(false);
    const [isLoadingRemainingProjects, setIsLoadingRemainingProjects] = useState(false);
    const [isFiling, setIsFiling] = useState(false);
    const [selectedProject, setSelectedProject] = useState<IProjectData | undefined>(undefined);
    const [projectSettings, setProjectSettings] = useState<ProjectSettingsResponse | null>(null);
    const [myProjects, setMyProjects] = useState<IProjectData[]>([]);
    const [projects, setProjects] = useState<IProjectData[]>([]);
    const [emails, setEmails] = useState<EmailListItem[]>([]);
    const [selectedEmailIds, setSelectedEmailIds] = useState<Set<string>>(new Set());
    const [loadedEmails, setLoadedEmails] = useState<EmailListItem[]>([]);
    const [emailPagingItemCount, setEmailPagingItemCount] = useState(0);
    const [folderId, setFolderId] = useState<string | null>(null);
    const [folderName, setFolderName] = useState("");
    const [emailOffsetToken, setEmailOffsetToken] = useState<string | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInputValue, setSearchInputValue] = useState("");
    const [isSubmitPromptHidden, setIsSubmitPromptHidden] = useState(false);
    const [filingProgressProcessed, setFilingProgressProcessed] = useState(0);
    const [filingProgressTotal, setFilingProgressTotal] = useState(0);
    const [reloadToken, setReloadToken] = useState(0);
    const [isSubfolderEnabled, setIsSubfolderEnabled] = useState(false);
    const [subfolders, setSubfolders] = useState<NewformaFolderTreeNode[]>([]);
    const [selectedSubfolder, setSelectedSubfolder] = useState<NewformaFolderTreeNode | undefined>(undefined);
    const [isReloadingSubfolders, setIsReloadingSubfolders] = useState(false);

    const isMountedRef = useRef(false);
    const isInitialLoadCompleteRef = useRef(false);
    const isRefreshingProjectsRef = useRef(false);
    const folderIdRef = useRef<string | null>(null);
    const emailOffsetTokenRef = useRef<string | undefined>(undefined);
    const searchTermRef = useRef("");
    const isLoadingMoreEmailsRef = useRef(false);
    const loadedEmailsRef = useRef<EmailListItem[]>([]);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const currentEmailItemRef = useRef<EmailListItem | null>(null);
    const mailboxItemRef = useRef<MailboxItem | null>(props.mailboxItem);
    const searchRequestIdRef = useRef(0);
    const globalProjectsLoadIdRef = useRef(0);
    const subfolderRequestIdRef = useRef(0);
    const projectSettingsRequestIdRef = useRef(0);
    const [teachingBubbleStep, setTeachingBubbleStep] = useState(1);
    const [teachingBubbleTarget, setTeachingBubbleTarget] = useState<HTMLElement | null>(null);

    const projectTags = React.useMemo(
        () =>
            projects.map((project) => ({
                key: project.nrn,
                name: project.displayName || project.name,
                additionalValue: project.autoFileMailboxFolderName ?? project.name,
            })),
        [projects]
    );

    const myProjectTags = React.useMemo(
        () =>
            myProjects.map((project) => ({
                key: project.nrn,
                name: project.displayName || project.name,
                additionalValue: project.autoFileMailboxFolderName ?? project.name,
            })),
        [myProjects]
    );

    useEffect(() => {
        loadedEmailsRef.current = loadedEmails;
    }, [loadedEmails]);

    // Initialize component
    useEffect(() => {
        isMountedRef.current = true;
        isInitialLoadCompleteRef.current = false;
        props.onSetNavigationPage(AppPage.FileMultipleEmail);

        const initialize = async () => {
            try {
                if (props.shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache) {
                    await props.invalidateCacheService.invalidateCache(
                        ProjectsCacheKeys.myProjectsCacheName,
                        ProjectsCacheKeys.globalProjectsCacheName
                    );
                }

                // Load projects and emails in parallel
                const [projectsResult] = await Promise.all([loadMyProjects(), loadOutlookContextAndEmails()]);

                // My projects and emails are loaded; unblock the UI before loading the remaining
                // global projects in the background so filing does not wait on the global load.
                isInitialLoadCompleteRef.current = true;
                setIsLoadingProjects(false);

                if (projectsResult.myProjects.length === 0) {
                    await refreshMyProjects();
                } else {
                    props.hideNoProjectsToast();
                    if (!props.isGlobalProjectSearchSupported) {
                        await loadGlobalProjects(projectsResult.paging);
                    }
                }
            } catch (error) {
                if (isExpiredSessionOrTokenError(error)) {
                    props.onExpiredSession();
                    return;
                }

                props.logger.error("Error initializing FileMultipleEmailFluent9", error);
            } finally {
                setIsLoadingProjects(false);
            }
        };

        initialize();

        const removeCacheRefreshedListener = props.messenger.on<{ myProjects: Project[]; projects: Project[] }>(
            MessageTypes.CacheRefreshed,
            (data) => {
                if (!isMountedRef.current || !isInitialLoadCompleteRef.current || !data?.myProjects) {
                    return;
                }

                const mappedMyProjects = mapProjects(data.myProjects);
                setMyProjects(mappedMyProjects);
                setProjects(mapProjects(data.projects || data.myProjects));
            }
        );

        return () => {
            isMountedRef.current = false;
            isInitialLoadCompleteRef.current = false;
            removeCacheRefreshedListener();
        };
    }, []);

    // Resolves a translated string, but falls back to the bundled English text when the
    // localization store has not finished initializing yet (no active language). This
    // prevents the raw "Missing translationId: ..." placeholder from leaking into toasts
    // when an error fires during a rapid close/reopen of the add-in.
    const translateWithFallback = useCallback(
        (translationId: string): string => {
            if (props.activeLanguage) {
                return props.translate(translationId) as string;
            }
            return getEnglishTranslation(translationId) ?? "";
        },
        [props]
    );

    const loadMyProjects = useCallback(async (): Promise<{ myProjects: IProjectData[]; paging?: Paging }> => {
        try {
            const response = await props.projectsService.getMyProjects();
            const mappedProjects = mapProjects(response.projects || []);
            if (isMountedRef.current) {
                setMyProjects(mappedProjects);
                setProjects(mappedProjects);
            }

            return {
                myProjects: mappedProjects,
                paging: response.paging,
            };
        } catch (error) {
            if (isExpiredSessionOrTokenError(error)) {
                props.onExpiredSession();
                return { myProjects: [] };
            }

            props.logger.error("Error loading projects", error);
            props.onShowToast(props.translate("SHARED.ERRORS.LOADING_PROJECTS_GENERIC") as string, "error");
            return { myProjects: [] };
        }
    }, [props]);

    const loadGlobalProjects = useCallback(
        async (paging?: Paging): Promise<void> => {
            if (props.isGlobalProjectSearchSupported || !props.officeRoamingSettings.getShowAllGlobalProjects()) {
                return;
            }

            const currentLoadId = ++globalProjectsLoadIdRef.current;
            setIsLoadingRemainingProjects(true);

            try {
                let nextPaging: Paging | undefined = paging;

                while (nextPaging?.offsetToken) {
                    const globalProjectsResponse = await props.projectsService.getGlobalProjects(nextPaging);
                    const mappedGlobalProjects = mapProjects(globalProjectsResponse.projects || []);

                    if (!isMountedRef.current || currentLoadId !== globalProjectsLoadIdRef.current) {
                        return;
                    }

                    setProjects((previousProjects) => {
                        const existingNrns = new Set(previousProjects.map((project) => project.nrn));
                        const uniqueGlobalProjects = mappedGlobalProjects.filter(
                            (project) => !existingNrns.has(project.nrn)
                        );
                        return [...previousProjects, ...uniqueGlobalProjects];
                    });

                    nextPaging = globalProjectsResponse.paging;
                }
            } catch (error) {
                if (!isMountedRef.current || currentLoadId !== globalProjectsLoadIdRef.current) {
                    return;
                }

                if (isExpiredSessionOrTokenError(error)) {
                    props.onExpiredSession();
                    return;
                }

                props.logger.error("Error loading global projects", error);
                props.onShowToast(props.translate("SHARED.ERRORS.LOADING_PROJECTS_GENERIC") as string, "error");
            } finally {
                if (isMountedRef.current && currentLoadId === globalProjectsLoadIdRef.current) {
                    setIsLoadingRemainingProjects(false);
                }
            }
        },
        [props]
    );

    const refreshMyProjects = useCallback(async (): Promise<void> => {
        if (isRefreshingProjectsRef.current) {
            return;
        }

        isRefreshingProjectsRef.current = true;
        globalProjectsLoadIdRef.current += 1;
        setIsRefreshingProjects(true);
        props.logger.info("FileMultipleEmailFluent9 refreshing empty my projects cache");
        subfolderRequestIdRef.current += 1;
        projectSettingsRequestIdRef.current += 1;
        setSelectedProject(undefined);
        setProjectSettings(null);
        setIsSubfolderEnabled(false);
        setIsReloadingSubfolders(false);
        setSubfolders([]);
        setSelectedSubfolder(undefined);
        setReloadToken((previousToken) => previousToken + 1);
        let refreshedProjectsResult: { myProjects: IProjectData[]; paging?: Paging } = { myProjects: [] };
        try {
            if (props.isGlobalProjectSearchSupported) {
                await props.invalidateCacheService.invalidateCache(ProjectsCacheKeys.myProjectsCacheName);
            } else {
                await props.invalidateCacheService.invalidateCache(
                    ProjectsCacheKeys.myProjectsCacheName,
                    ProjectsCacheKeys.globalProjectsCacheName
                );
            }

            refreshedProjectsResult = await loadMyProjects();
        } finally {
            isRefreshingProjectsRef.current = false;
            if (isMountedRef.current) {
                setIsRefreshingProjects(false);
            }
        }

        if (!props.isGlobalProjectSearchSupported) {
            await loadGlobalProjects(refreshedProjectsResult.paging);
        }

        if (refreshedProjectsResult.myProjects.length === 0) {
            props.showNoProjectsToast();
            props.onShowToast(translateWithFallback("SHARED.ERRORS.NO_PROJECTS"), "error");
        } else {
            props.hideNoProjectsToast();
        }
    }, [loadGlobalProjects, loadMyProjects, props, translateWithFallback]);

    const moveSelectedEmailToTopOfList = useCallback(
        (emailItems: EmailListItem[], currentlySelectedEmail: EmailListItem): EmailListItem[] => {
            const emailsWithoutSelected = emailItems.filter((email) => email.id !== currentlySelectedEmail.id);
            return [currentlySelectedEmail, ...emailsWithoutSelected];
        },
        []
    );

    const loadEmails = useCallback(
        async (searchValue: string | null, offsetToken: string | null, isLoadingMore: boolean): Promise<void> => {
            const currentFolderId = folderIdRef.current;
            const currentEmailItem = currentEmailItemRef.current;
            const requestId = ++searchRequestIdRef.current;

            if (!currentFolderId || !currentEmailItem) {
                return;
            }

            try {
                setIsLoadingEmails(!isLoadingMore);
                setIsLoadingMoreEmails(isLoadingMore);

                const emailResponse = await props.msGraphApiService.getFolderEmails(
                    currentFolderId,
                    offsetToken,
                    searchValue
                );

                if (!isMountedRef.current || requestId !== searchRequestIdRef.current) {
                    return;
                }

                const nextEmails = isLoadingMore
                    ? emailResponse.items
                    : moveSelectedEmailToTopOfList(emailResponse.items, currentEmailItem);

                if (isLoadingMore) {
                    setEmails((prevEmails) => {
                        const existingIds = new Set(prevEmails.map((email) => email.id));
                        return [...prevEmails, ...nextEmails.filter((email) => !existingIds.has(email.id))];
                    });
                    setLoadedEmails((prevEmails) => {
                        const existingIds = new Set(prevEmails.map((email) => email.id));
                        return [...prevEmails, ...emailResponse.items.filter((email) => !existingIds.has(email.id))];
                    });
                    setEmailPagingItemCount((prevCount) => prevCount + emailResponse.items.length);
                } else {
                    setEmails(nextEmails);
                    setSelectedEmailIds((prevSelectedEmailIds) =>
                        getNextSelectedEmailIds(prevSelectedEmailIds, new Set([currentEmailItem.id]))
                    );
                    setLoadedEmails(emailResponse.items);
                    setEmailPagingItemCount(emailResponse.items.length);
                }

                emailOffsetTokenRef.current = emailResponse.paging.offsetToken;
                setEmailOffsetToken(emailResponse.paging.offsetToken);
            } catch (error) {
                if (isExpiredSessionOrTokenError(error)) {
                    props.onExpiredSession();
                } else {
                    props.logger.error("Error loading emails", error);
                    props.onShowToast(translateWithFallback("FILE_MULTIPLE_EMAIL.ERROR_LOADING_EMAILS"), "error");
                }
            } finally {
                if (isMountedRef.current && requestId === searchRequestIdRef.current) {
                    setIsLoadingEmails(false);
                    setIsLoadingMoreEmails(false);
                    isLoadingMoreEmailsRef.current = false;
                }
            }
        },
        [moveSelectedEmailToTopOfList, props]
    );

    const loadOutlookContextAndEmails = useCallback(async () => {
        try {
            setIsLoadingEmails(true);
            const [folderId, currentEmailItem] = await Promise.all([
                props.msGraphApiService.getCurrentFolderId(),
                props.msGraphApiService.getCurrentMessageAsEmailListItem(),
            ]);

            if (!isMountedRef.current) return;

            const folderName = await props.msGraphApiService.getCachedFolderName(folderId);

            if (!isMountedRef.current) return;

            setFolderId(folderId);
            folderIdRef.current = folderId;
            setFolderName(folderName);
            currentEmailItemRef.current = currentEmailItem;

            // Load emails
            if (folderId && currentEmailItem) {
                await loadEmails(searchTermRef.current.trim() || null, null, false);
            }
        } catch (error) {
            if (isExpiredSessionOrTokenError(error)) {
                props.onExpiredSession();
                return;
            }

            props.logger.error("Error loading outlook context and emails", error);
            props.onShowToast(translateWithFallback("FILE_MULTIPLE_EMAIL.ERROR_LOADING_EMAILS"), "error");
        } finally {
            setIsLoadingEmails(false);
        }
    }, [props, loadEmails]);

    useEffect(() => {
        if (mailboxItemRef.current === props.mailboxItem) {
            return;
        }

        mailboxItemRef.current = props.mailboxItem;
        if (!props.mailboxItem) {
            return;
        }

        const updateCurrentEmail = async () => {
            const [currentFolderId, currentEmailItem] = await Promise.all([
                props.msGraphApiService.getCurrentFolderId(),
                props.msGraphApiService.getCurrentMessageAsEmailListItem(),
            ]);

            if (!isMountedRef.current) {
                return;
            }

            if (currentFolderId !== folderIdRef.current) {
                setEmails([]);
                setSelectedEmailIds((prevSelectedEmailIds) => getNextSelectedEmailIds(prevSelectedEmailIds, new Set()));
                setLoadedEmails([]);
                setEmailPagingItemCount(0);
                emailOffsetTokenRef.current = undefined;
                setEmailOffsetToken(undefined);
                await loadOutlookContextAndEmails();
                return;
            }

            const previousCurrentEmailId = currentEmailItemRef.current?.id;
            currentEmailItemRef.current = currentEmailItem;

            setSelectedEmailIds((prevSelectedEmailIds) => {
                const nextSelectedEmailIds = new Set(prevSelectedEmailIds);
                if (previousCurrentEmailId) {
                    nextSelectedEmailIds.delete(previousCurrentEmailId);
                }
                nextSelectedEmailIds.add(currentEmailItem.id);
                return getNextSelectedEmailIds(prevSelectedEmailIds, nextSelectedEmailIds);
            });
            setEmails(moveSelectedEmailToTopOfList(loadedEmailsRef.current, currentEmailItem));
        };

        void updateCurrentEmail();
    }, [loadOutlookContextAndEmails, moveSelectedEmailToTopOfList, props.mailboxItem, props.msGraphApiService]);

    const handleLoadMoreEmails = useCallback(async () => {
        const currentFolderId = folderIdRef.current;
        const currentOffsetToken = emailOffsetTokenRef.current;

        if (!currentFolderId || !currentOffsetToken || isLoadingEmails || isLoadingMoreEmailsRef.current) {
            return;
        }

        try {
            isLoadingMoreEmailsRef.current = true;
            await loadEmails(searchTermRef.current.trim() || null, currentOffsetToken, true);
        } catch (error) {
            props.logger.error("Error loading more emails", error);
            props.onShowToast(translateWithFallback("FILE_MULTIPLE_EMAIL.ERROR_LOADING_EMAILS"), "error");
        } finally {
            isLoadingMoreEmailsRef.current = false;
        }
    }, [isLoadingEmails, loadEmails, props]);

    const isCurrentRequest = useCallback(
        (requestId: number) => isMountedRef.current && requestId === subfolderRequestIdRef.current,
        []
    );

    const loadSubfolders = useCallback(
        async (nrn: string, sortBy?: string, emailFolderName?: string): Promise<void> => {
            const requestId = ++subfolderRequestIdRef.current;
            setIsReloadingSubfolders(true);

            try {
                const rootFolder = await props.projectsService.getProjectFiles(
                    nrn,
                    FOLDER_FETCH_PAGE_MAX_SIZE,
                    false,
                    true,
                    sortBy,
                    true
                );

                const rootItem = rootFolder?.items?.[0];
                if (!rootItem) {
                    if (isCurrentRequest(requestId)) {
                        setSubfolders([]);
                    }
                    return;
                }
                const emailFolderNrn = buildEmailFolderNrn(rootItem.nrn, nrn, emailFolderName ?? "email");
                const response = await props.projectsService.getProjectFiles(
                    emailFolderNrn,
                    FOLDER_FETCH_PAGE_MAX_SIZE,
                    true,
                    true,
                    sortBy,
                    true
                );

                if (isCurrentRequest(requestId)) {
                    setSubfolders(ProjectHelper.mapProjectFolders(response?.items));
                }
            } catch (error) {
                if (isCurrentRequest(requestId)) {
                    setSubfolders([]);
                    props.logger.error("Error loading project subfolders", error);
                }
            } finally {
                if (isCurrentRequest(requestId)) {
                    setIsReloadingSubfolders(false);
                }
            }
        },
        [props.logger, props.projectsService, isCurrentRequest]
    );

    const loadProjectSettings = useCallback(
        async (projectNrn: string): Promise<ProjectSettingsResponse | null | undefined> => {
            const requestId = ++projectSettingsRequestIdRef.current;
            try {
                const projectSettings = await props.projectsService.getProjectSettings(projectNrn, "Email");
                if (!isMountedRef.current || requestId !== projectSettingsRequestIdRef.current) {
                    return undefined;
                }
                setProjectSettings(projectSettings);
                return projectSettings;
            } catch (error) {
                props.logger.error("Error loading project settings", error);
                return null;
            }
        },
        [props.logger, props.projectsService]
    );

    const handleProjectChanged = useCallback(
        async (project?: IProjectData) => {
            subfolderRequestIdRef.current += 1;
            projectSettingsRequestIdRef.current += 1;
            setSelectedProject(project);
            setIsSubfolderEnabled(false);
            setIsReloadingSubfolders(false);
            setSubfolders([]);
            setSelectedSubfolder(undefined);
            setProjectSettings(null);
            if (project) {
                const nextProjectSettings = await loadProjectSettings(project.nrn);
                if (nextProjectSettings !== undefined) {
                    await loadSubfolders(
                        project.nrn,
                        SUBFOLDER_SORT_BY,
                        nextProjectSettings?.emails?.projectEmailFolder
                    );
                }
            }
        },
        [loadProjectSettings, loadSubfolders]
    );

    const handlePaginatedProjectChanged = useCallback(
        async (project: ITag | null) => {
            subfolderRequestIdRef.current += 1;
            projectSettingsRequestIdRef.current += 1;
            setProjectSettings(null);
            if (!project) {
                setSelectedProject(undefined);
                setIsSubfolderEnabled(false);
                setIsReloadingSubfolders(false);
                setSubfolders([]);
                setSelectedSubfolder(undefined);
                return;
            }

            const matchedProject = projects.find((item) => item.nrn === String(project.key));
            const nextSelectedProject = matchedProject ?? {
                nrn: String(project.key),
                name: project.name,
                displayName: project.name,
            };
            setSelectedProject(nextSelectedProject);
            setIsSubfolderEnabled(false);
            setIsReloadingSubfolders(false);
            setSubfolders([]);
            setSelectedSubfolder(undefined);
            const nextProjectSettings = await loadProjectSettings(nextSelectedProject.nrn);
            if (nextProjectSettings !== undefined) {
                await loadSubfolders(
                    nextSelectedProject.nrn,
                    SUBFOLDER_SORT_BY,
                    nextProjectSettings?.emails?.projectEmailFolder
                );
            }
        },
        [loadProjectSettings, loadSubfolders, projects]
    );

    const handleEmailSelectionChanged = useCallback((emailId: string, isSelected: boolean) => {
        setSelectedEmailIds((prevSelectedEmailIds) => {
            const nextSelectedEmailIds = new Set(prevSelectedEmailIds);
            if (isSelected) {
                nextSelectedEmailIds.add(emailId);
            } else {
                nextSelectedEmailIds.delete(emailId);
            }
            return getNextSelectedEmailIds(prevSelectedEmailIds, nextSelectedEmailIds);
        });
    }, []);

    const handleSearchChanged = useCallback((value: string) => {
        setSearchInputValue(value);
    }, []);

    const submitEmailSearch = useCallback(
        async (value: string) => {
            if (!folderIdRef.current || !currentEmailItemRef.current) {
                return;
            }

            const nextSearchTerm = value.trim();
            setSearchTerm(nextSearchTerm);
            setSearchInputValue(nextSearchTerm);
            searchTermRef.current = nextSearchTerm;
            emailOffsetTokenRef.current = undefined;
            setEmailOffsetToken(undefined);
            setEmails([]);
            setSelectedEmailIds((prevSelectedEmailIds) => getNextSelectedEmailIds(prevSelectedEmailIds, new Set()));
            setLoadedEmails([]);
            setEmailPagingItemCount(0);
            await loadEmails(nextSearchTerm || null, null, false);
        },
        [loadEmails]
    );

    const handleSearchKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== "Enter") {
                return;
            }

            event.preventDefault();
            void submitEmailSearch(searchInputValue);
        },
        [searchInputValue, submitEmailSearch]
    );

    const handleSearchIconClick = useCallback(() => {
        void submitEmailSearch(searchInputValue);
    }, [searchInputValue, submitEmailSearch]);

    const handleClearSearch = useCallback(() => {
        void submitEmailSearch("");
    }, [submitEmailSearch]);

    const handleSelectedEmailsCountChanged = useCallback((selectedCount: number) => {
        setIsSubmitPromptHidden(selectedCount > 0);
    }, []);

    const handleReloadProjects = useCallback(async () => {
        if (
            !isInitialLoadCompleteRef.current ||
            isLoadingProjects ||
            isRefreshingProjectsRef.current ||
            isLoadingRemainingProjects
        ) {
            props.logger.info("FileMultipleEmailFluent9 ignored project reload while projects are already loading");
            return;
        }

        await refreshMyProjects();
    }, [isLoadingProjects, isLoadingRemainingProjects, props.logger, refreshMyProjects]);

    const handleReloadEmails = useCallback(async () => {
        try {
            setIsReloadingEmails(true);
            setSearchTerm("");
            setSearchInputValue("");
            searchTermRef.current = "";
            await loadOutlookContextAndEmails();
        } finally {
            if (isMountedRef.current) {
                setIsReloadingEmails(false);
            }
        }
    }, [loadOutlookContextAndEmails]);

    const handleSelectAll = useCallback(() => {
        const visibleIds = new Set(emails.map((email) => email.id));
        const shouldSelectAll = emails.some((email) => !selectedEmailIds.has(email.id));

        setSelectedEmailIds((prevSelectedEmailIds) => {
            const nextSelectedEmailIds = new Set(prevSelectedEmailIds);
            visibleIds.forEach((emailId) => {
                if (shouldSelectAll) {
                    nextSelectedEmailIds.add(emailId);
                } else {
                    nextSelectedEmailIds.delete(emailId);
                }
            });
            return getNextSelectedEmailIds(prevSelectedEmailIds, nextSelectedEmailIds);
        });
    }, [emails, selectedEmailIds]);

    const handleSubfolderToggleChanged = useCallback(
        (isChecked: boolean) => {
            setIsSubfolderEnabled(isChecked);
            setSelectedSubfolder(undefined);
        },
        [loadSubfolders, selectedProject]
    );

    const handleReloadSubfolders = useCallback(() => {
        if (!selectedProject) {
            return;
        }

        setSelectedSubfolder(undefined);
        void loadSubfolders(selectedProject.nrn, SUBFOLDER_SORT_BY, projectSettings?.emails?.projectEmailFolder);
    }, [loadSubfolders, projectSettings, selectedProject]);

    // Creates (or finds, if it already exists) the real Outlook subfolder chain nested inside the
    // project's own "Newforma - Items to File" folder, returning the leaf folder's Graph id.
    // `subfolderNamePath` is the ordered list of folder names from the project root down to the
    // selected NPC subfolder, e.g. ["Subfolder1", "Subfolder2"].
    const ensureSubfolderCreated = useCallback(
        async (projectDisplayName: string, subfolderNamePath: string[]): Promise<string | undefined> => {
            props.logger.info(
                `ensureSubfolderCreated - start: project="${projectDisplayName}" subfolderPath="${subfolderNamePath.join(
                    "/"
                )}"`
            );
            try {
                const parentFolderId = await waitForNewformaFolderId();
                if (!parentFolderId) {
                    props.logger.error("ensureSubfolderCreated - newformaFolderId is not available.");
                    return undefined;
                }
                const projectFolderId = await props.msGraphApiService.createSubfolder(
                    parentFolderId,
                    projectDisplayName
                );
                if (!projectFolderId) {
                    props.logger.error("ensureSubfolderCreated - error creating/finding project folder.");
                    return undefined;
                }
                props.logger.info(`ensureSubfolderCreated - project folder id=${projectFolderId}`);
                const subfolderId = await props.msGraphApiService.createNestedSubfolders(
                    projectFolderId,
                    subfolderNamePath
                );
                if (!subfolderId) {
                    props.logger.error("ensureSubfolderCreated - error creating subfolder chain.");
                    return undefined;
                }
                props.logger.info(`ensureSubfolderCreated - subfolder id=${subfolderId}`);
                return subfolderId;
            } catch (error) {
                props.logger.error("ensureSubfolderCreated failed", error);
                return undefined;
            }
        },
        [props.logger, props.msGraphApiService]
    );

    const handleSubmit = useCallback(async () => {
        const selectedEmailIdsForSubmit = Array.from(selectedEmailIds);

        if (!selectedEmailIdsForSubmit.length) {
            props.onShowToast(props.translate("FILE_MULTIPLE_EMAIL.NO_EMAILS_SELECTED") as string, "error");
            return;
        }

        if (!selectedProject) {
            props.onShowToast(props.translate("FILE_MULTIPLE_EMAIL.NO_PROJECT_SELECTED") as string, "error");
            return;
        }

        const isFilingIntoSubfolder = isSubfolderEnabled && !!selectedSubfolder;
        const projectDisplayName = selectedProject.autoFileMailboxFolderName ?? selectedProject.name;
        const subfolderNamePath = selectedSubfolder
            ? ProjectHelper.getFolderNamePath(subfolders, selectedSubfolder.id) ?? [selectedSubfolder.name]
            : [];

        props.logger.info(
            `handleSubmit - start: isSubfolderEnabled=${isSubfolderEnabled} selectedSubfolder=${
                selectedSubfolder ? `"${subfolderNamePath.join("/")}"` : "none"
            } isFilingIntoSubfolder=${isFilingIntoSubfolder}`
        );

        // Disable the submit button immediately, before any async subfolder creation.
        setIsFiling(true);
        setFilingProgressProcessed(0);
        setFilingProgressTotal(selectedEmailIdsForSubmit.length);

        let subfolderGraphFolderId: string | undefined;
        if (isFilingIntoSubfolder) {
            subfolderGraphFolderId = await ensureSubfolderCreated(projectDisplayName, subfolderNamePath);
            if (!subfolderGraphFolderId) {
                props.logger.error("handleSubmit - aborting: ensureSubfolderCreated did not return a folder id.");
                props.onShowToast(props.translate("FILE_MULTIPLE_EMAIL.ERROR_FILING_EMAIL_GENERIC") as string, "error");
                setIsFiling(false);
                setFilingProgressProcessed(0);
                setFilingProgressTotal(0);
                return;
            }
        }

        try {
            const fileConversation = props.officeRoamingSettings.getFileEmailConversation();
            const deleteAfterFiling = props.deleteEmailAfterFiling;

            const selectedProjectTag: ITag = {
                key: selectedProject.nrn,
                name: selectedProject.displayName || selectedProject.name,
            };

            // When filing against the project NRN the filing API always places the mailbox copy
            // directly under the project's own folder in "Newforma - Items to File". To land the
            // email in the selected subfolder instead, target that Outlook folder directly via its
            // email-folder NRN (same mechanism Send and File uses). The server then copies or
            // moves the message there according to the user's post-filing setting.
            const subfolderFilingLocation: ITag | undefined = subfolderGraphFolderId
                ? {
                      key: `nrn:email:folder:ms-graph:${encodeURIComponent(subfolderGraphFolderId)}`,
                      name: subfolderNamePath.join("/"),
                  }
                : undefined;

            props.logger.info(
                `handleSubmit - calling fileMultipleEmails: project="${projectDisplayName}" subfolder=${
                    isFilingIntoSubfolder ? `"${subfolderNamePath.join("/")}"` : "none"
                } destination=${subfolderFilingLocation?.key ?? selectedProjectTag.key} emailCount=${
                    selectedEmailIdsForSubmit.length
                }`
            );

            const failedIds = await props.emailApiService.fileMultipleEmails(
                selectedEmailIdsForSubmit,
                selectedProjectTag,
                fileConversation,
                projectDisplayName,
                props.culture,
                subfolderFilingLocation,
                () => setFilingProgressProcessed((processedCount) => processedCount + 1),
                isFilingIntoSubfolder ? subfolderNamePath.join("/") : undefined
            );

            props.logger.info(
                `handleSubmit - fileMultipleEmails result: ${selectedEmailIdsForSubmit.length - failedIds.length}/${
                    selectedEmailIdsForSubmit.length
                } succeeded, failedIds=${JSON.stringify(failedIds)}`
            );

            const successfulIds = selectedEmailIdsForSubmit.filter((id) => !failedIds.includes(id));

            if (deleteAfterFiling) {
                setEmails((prevEmails) => prevEmails.filter((e) => !successfulIds.includes(e.id)));
                setLoadedEmails((prevEmails) => prevEmails.filter((e) => !successfulIds.includes(e.id)));
            } else {
                setEmails((prevEmails) =>
                    prevEmails.map((email) => (successfulIds.includes(email.id) ? { ...email, isFiled: true } : email))
                );
                setLoadedEmails((prevEmails) =>
                    prevEmails.map((email) => (successfulIds.includes(email.id) ? { ...email, isFiled: true } : email))
                );
            }

            if (failedIds.length) {
                const errorMessage = (props.translate("FILE_MULTIPLE_EMAIL.FILING_ERROR") as string)
                    .replace("[[count]]", failedIds.length.toString())
                    .replace("[[total]]", selectedEmailIdsForSubmit.length.toString());
                props.onShowToast(errorMessage, "error");
            } else {
                const projectDisplayName =
                    selectedProject.displayName ||
                    (selectedProject.number
                        ? `${selectedProject.number} - ${selectedProject.name}`
                        : selectedProject.name);
                const successMessageKey = isFilingIntoSubfolder
                    ? "FILE_MULTIPLE_EMAIL.FILING_SUCCESS_SUBFOLDER"
                    : "FILE_MULTIPLE_EMAIL.FILING_SUCCESS";
                const successMessageTemplateRaw = props.translate(successMessageKey) as string;
                const successMessageTemplate =
                    successMessageTemplateRaw === successMessageKey
                        ? isFilingIntoSubfolder
                            ? "Filed [[count]] email(s) to project [[projectName]] under subfolder [[subfolderName]]"
                            : "Filed [[count]] email(s) to project [[projectName]]"
                        : successMessageTemplateRaw;
                const subfolderDisplayName = selectedSubfolder?.name ?? "";
                const successMessage = successMessageTemplate
                    .replace("[[count]]", successfulIds.length.toString())
                    .replace("[[projectName]]", () => projectDisplayName)
                    .replace("[[subfolderName]]", () => subfolderDisplayName);
                props.onShowToast(successMessage, "success");
            }

            props.analyticsManager.recordEvent(
                AnalyticsCategoryType.UserActions,
                AnalyticsActionType.FileMultipleEmail
            );
        } catch (error) {
            if (isExpiredSessionOrTokenError(error)) {
                props.onExpiredSession();
                return;
            }
            props.logger.error("Error filing emails", error);
            props.onShowToast(props.translate("FILE_MULTIPLE_EMAIL.ERROR_FILING_EMAIL_GENERIC") as string, "error");
        } finally {
            setIsFiling(false);
            setFilingProgressProcessed(0);
            setFilingProgressTotal(0);
            setSelectedEmailIds((prevSelectedEmailIds) => getNextSelectedEmailIds(prevSelectedEmailIds, new Set()));
        }
    }, [
        selectedEmailIds,
        selectedProject,
        isSubfolderEnabled,
        selectedSubfolder,
        subfolders,
        ensureSubfolderCreated,
        props,
    ]);

    const selectedEmailsCount = selectedEmailIds.size;
    const shouldShowSubfolderRow =
        props.isFileEmailIntoSubfolderSupported && !!selectedProject && !!projectSettings?.emails?.hasEmailSubfolders;
    const isProjectPickerLoading = isLoadingProjects || isRefreshingProjects;
    const isPaginatedReloadDisabled =
        isProjectPickerLoading ||
        isLoadingRemainingProjects ||
        !isInitialLoadCompleteRef.current ||
        isRefreshingProjectsRef.current;
    const isSubmitDisabled =
        isFiling ||
        !selectedProject ||
        selectedEmailsCount === 0 ||
        isProjectPickerLoading ||
        isLoadingEmails ||
        isReloadingEmails;
    const shouldShowTeachingBubbles =
        props.shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache && teachingBubbleStep <= 3;

    const getTeachingBubbleTargetSelector = (step: number): string => {
        switch (step) {
            case 1:
                return ".newforma-navHeaderComponent .newforma-navPrefix";
            case 2:
                return ".newforma-suggestedProjectPicker";
            case 3:
                return ".newforma-emailListRowItemComponentFluent9:first-child";
            default:
                return ".newforma-navHeaderComponent .newforma-navPrefix";
        }
    };

    useEffect(() => {
        if (!shouldShowTeachingBubbles || isLoadingProjects) {
            setTeachingBubbleTarget(null);
            return;
        }

        const target = document.querySelector<HTMLElement>(getTeachingBubbleTargetSelector(teachingBubbleStep));
        setTeachingBubbleTarget(target || rootRef.current);
    }, [emails.length, isLoadingProjects, shouldShowTeachingBubbles, teachingBubbleStep]);

    const dismissTeachingBubble = useCallback(() => {
        setTeachingBubbleStep(4);
        props.onDismissTeachingBubble();
    }, [props]);

    const onTeachingBubblePrimaryClick = useCallback(() => {
        if (teachingBubbleStep < 3) {
            setTeachingBubbleStep((step) => step + 1);
            return;
        }

        dismissTeachingBubble();
    }, [dismissTeachingBubble, teachingBubbleStep]);

    const renderTeachingBubbleBody = (): React.ReactNode => {
        switch (teachingBubbleStep) {
            case 1:
                return (
                    <>
                        <span>{props.translate("TEACHING_BUBBLES_v1.BUBBLE_1.PART_1") as string}</span>
                        <span className={styles.teachingBubbleBoldText}>
                            {props.translate("TEACHING_BUBBLES_v1.BUBBLE_1.PART_2") as string}
                        </span>
                        <span>{props.translate("TEACHING_BUBBLES_v1.BUBBLE_1.PART_3") as string}</span>
                        <span className={styles.teachingBubbleBoldText}>
                            {props.translate("TEACHING_BUBBLES_v1.BUBBLE_1.PART_4") as string}
                        </span>
                        <span>{props.translate("TEACHING_BUBBLES_v1.BUBBLE_1.PART_5") as string}</span>
                        <span className={styles.teachingBubbleBoldText}>
                            {props.translate("TEACHING_BUBBLES_v1.BUBBLE_1.PART_6") as string}
                        </span>
                        <span>{props.translate("TEACHING_BUBBLES_v1.BUBBLE_1.PART_7") as string}</span>
                    </>
                );
            case 2:
                return (
                    <>
                        <span className={styles.teachingBubbleBoldText}>
                            {props.translate("TEACHING_BUBBLES_v1.BUBBLE_2.PART_1") as string}
                        </span>
                        <span>{props.translate("TEACHING_BUBBLES_v1.BUBBLE_2.PART_2") as string}</span>
                    </>
                );
            case 3:
                return (
                    <>
                        <span className={styles.teachingBubbleBoldText}>
                            {props.translate("TEACHING_BUBBLES_v1.BUBBLE_3.PART_1") as string}
                        </span>
                        <span>{props.translate("TEACHING_BUBBLES_v1.BUBBLE_3.PART_2") as string}</span>
                    </>
                );
            default:
                return null;
        }
    };

    const renderMultipleEmailsTeachingBubble = (): JSX.Element | null => {
        if (!shouldShowTeachingBubbles || !teachingBubbleTarget) {
            return null;
        }

        const isLastStep = teachingBubbleStep === 3;
        const buttonText = props.translate(
            isLastStep ? "TEACHING_BUBBLES_v1.BUBBLE_INFO.PART_3" : "TEACHING_BUBBLES_v1.BUBBLE_INFO.PART_2"
        ) as string;
        const footerText = props.translate(`TEACHING_BUBBLES_v1.BUBBLE_INFO.PART_${teachingBubbleStep + 3}`) as string;
        const teachingBubbleTextColor = isDarkMode ? "black" : "white";
        const teachingBubbleStyles: Partial<ITeachingBubbleStyles> = {
            footer: {
                display: "flex",
                flexDirection: "row-reverse",
                ["& > .ms-StackItem > span"]: {
                    marginRight: "75px",
                },
                color: teachingBubbleTextColor,
            },
            content: {
                color: teachingBubbleTextColor,
            },
            subText: {
                color: teachingBubbleTextColor,
            },
            headline: {
                color: teachingBubbleTextColor,
            },
            closeButton: {
                [".ms-Icon"]: {
                    color: teachingBubbleTextColor,
                },
            },
        };

        return (
            <TeachingBubble
                calloutProps={{
                    directionalHint:
                        teachingBubbleStep === 1 ? DirectionalHint.bottomLeftEdge : DirectionalHint.bottomCenter,
                    className: "file-multiple-email-teaching-popover",
                    preventDismissOnScroll: true,
                    preventDismissOnResize: true,
                }}
                target={teachingBubbleTarget}
                isWide={false}
                hasCloseButton={true}
                closeButtonAriaLabel={props.translate("TEACHING_BUBBLES_v1.BUBBLE_INFO.PART_3") as string}
                primaryButtonProps={{
                    children: buttonText,
                    onClick: onTeachingBubblePrimaryClick,
                }}
                footerContent={footerText}
                onDismiss={dismissTeachingBubble}
                headline={props.translate("TEACHING_BUBBLES_v1.BUBBLE_INFO.PART_1") as string}
                styles={teachingBubbleStyles}
                ignoreExternalFocusing={true}
            >
                {renderTeachingBubbleBody()}
            </TeachingBubble>
        );
    };

    useEffect(() => {
        handleSelectedEmailsCountChanged(selectedEmailsCount);
    }, [handleSelectedEmailsCountChanged, selectedEmailsCount]);

    const visibleEmails = emails;

    const allVisibleSelected = visibleEmails.length > 0 && visibleEmails.every((e) => selectedEmailIds.has(e.id));
    const filingProgressDisplayCount = Math.min(filingProgressProcessed, filingProgressTotal);
    const filingProgressValue = filingProgressTotal > 0 ? filingProgressDisplayCount / filingProgressTotal : 0;
    const filingProgressTemplateRaw = props.translate("FILE_MULTIPLE_EMAIL.FILING_IN_PROGRESS_DETAILED") as string;
    const filingProgressTemplate =
        filingProgressTemplateRaw === "FILE_MULTIPLE_EMAIL.FILING_IN_PROGRESS_DETAILED"
            ? "Filing Emails [[processed]] of [[total]]"
            : filingProgressTemplateRaw;
    const filingProgressMessage = filingProgressTemplate
        .replace("[[processed]]", `${filingProgressDisplayCount}`)
        .replace("[[total]]", `${filingProgressTotal}`);

    const subfolderToggleContent = shouldShowSubfolderRow ? (
        <div data-testid="subfolder-toggle-row">
            <NewformaSwitch
                checked={isSubfolderEnabled}
                disabled={isLoadingEmails || isFiling}
                onChange={(_, data) => handleSubfolderToggleChanged(data.checked)}
                dataTestId="subfolder-toggle-switch"
                label={
                    <>
                        <Folder16Regular aria-hidden="true" />
                        <Text>{props.translate("FILE_MULTIPLE_EMAIL.SUBFOLDER_LABEL") as string}</Text>
                    </>
                }
            />
        </div>
    ) : (
        undefined
    );
    const subfolderPickerContent =
        shouldShowSubfolderRow && isSubfolderEnabled ? (
            <div className={styles.subfolderPickerContainer}>
                <SubfolderPickerFluent9
                    folders={subfolders}
                    selectedFolder={selectedSubfolder}
                    onFolderSelected={setSelectedSubfolder}
                    onReload={handleReloadSubfolders}
                    isLoading={isReloadingSubfolders}
                    disabled={isFiling || subfolders.length === 0}
                />
            </div>
        ) : (
            undefined
        );

    return (
        <FluentProvider theme={theme} style={{ display: "contents" }}>
            <div ref={rootRef} className={styles.root}>
                {renderMultipleEmailsTeachingBubble()}
                {/* Project Selection Section */}
                <div className={mergeClasses(styles.projectSection, "newforma-suggestedProjectPicker")}>
                    {props.isGlobalProjectSearchSupported ? (
                        <FilteredProjectPickerFluent9
                            selectedProject={selectedProject}
                            onProjectSelected={handleProjectChanged}
                            projectsService={props.projectsService}
                            smartFilingManager={props.smartFilingManager}
                            mailboxItem={props.mailboxItem}
                            disabled={isProjectPickerLoading || isFiling}
                            myProjects={myProjects}
                            isLoadingMyProjects={isProjectPickerLoading}
                            showAllGlobalProjects={props.officeRoamingSettings.getShowAllGlobalProjects()}
                            showGlobalSearchResults={true}
                            reloadToken={reloadToken}
                            onReloadProjects={handleReloadProjects}
                            reloadIconSize={16}
                            subfolderToggleContent={subfolderToggleContent}
                            subfolderPickerContent={subfolderPickerContent}
                            logger={props.logger}
                        />
                    ) : (
                        <PaginatedProjectPickerFluent9
                            logger={props.logger}
                            projects={projectTags}
                            myProjects={myProjectTags}
                            onProjectSelected={handlePaginatedProjectChanged}
                            disabled={isProjectPickerLoading || isFiling}
                            isLoadingProjects={isProjectPickerLoading}
                            isLoadingRemainingProjects={isLoadingRemainingProjects}
                            isReloadDisabled={isPaginatedReloadDisabled}
                            smartFilingManager={props.smartFilingManager}
                            mailboxItem={props.mailboxItem}
                            onRefresh={handleReloadProjects}
                            showAllGlobalProjects={props.officeRoamingSettings.getShowAllGlobalProjects()}
                            reloadToken={reloadToken}
                            reloadIconSize={16}
                            subfolderToggleContent={subfolderToggleContent}
                            subfolderPickerContent={subfolderPickerContent}
                        />
                    )}
                </div>

                {/* Search Section */}
                <div className={styles.searchSection}>
                    <Field
                        className={styles.searchField}
                        label={props.translate("FILE_MULTIPLE_EMAIL.SEARCH_BOX_LABEL") as string}
                    >
                        <Input
                            appearance={isDarkMode ? "filled-darker" : "outline"}
                            className={mergeClasses(styles.searchInput, isDarkMode && styles.searchInputDark)}
                            contentBefore={
                                <Button
                                    aria-label={props.translate("FILE_MULTIPLE_EMAIL.SEARCH_BOX_LABEL") as string}
                                    appearance="transparent"
                                    className={styles.searchClearButton}
                                    icon={<Search20Regular />}
                                    onClick={handleSearchIconClick}
                                    size="small"
                                    disabled={isLoadingEmails || isFiling}
                                />
                            }
                            contentAfter={
                                searchInputValue ? (
                                    <Button
                                        aria-label="Clear search"
                                        appearance="transparent"
                                        className={styles.searchClearButton}
                                        icon={<Dismiss16Regular />}
                                        onClick={handleClearSearch}
                                        size="small"
                                        disabled={isLoadingEmails || isFiling}
                                    />
                                ) : (
                                    undefined
                                )
                            }
                            placeholder={props.translate("FILE_MULTIPLE_EMAIL.SEARCH_PLACEHOLDER") as string}
                            value={searchInputValue}
                            autoComplete="one-time-code"
                            name="fme-search-nofill"
                            data-form-type="other"
                            data-lpignore="true"
                            onChange={(_, data) => handleSearchChanged(data.value)}
                            onKeyDown={handleSearchKeyDown}
                            disabled={isLoadingEmails || isFiling}
                            input={{ className: styles.searchInputField, "aria-autocomplete": "none" as any }}
                        />
                    </Field>
                    <div className={styles.actionLinkRow}>
                        <Link
                            className={styles.actionLink}
                            onClick={handleReloadEmails}
                            disabled={isLoadingEmails || isLoadingMoreEmails || isReloadingEmails || isFiling}
                        >
                            <ArrowClockwise16Regular />
                            {props.translate("SHARED.REFRESH_EMAILS") as string}
                        </Link>
                    </div>
                </div>

                <div className={styles.listHeader}>
                    <Link
                        className={styles.selectAllLink}
                        onClick={handleSelectAll}
                        disabled={isLoadingEmails || isFiling || visibleEmails.length === 0}
                    >
                        {
                            props.translate(
                                allVisibleSelected
                                    ? "FILE_MULTIPLE_EMAIL.DESELECT_ALL"
                                    : "FILE_MULTIPLE_EMAIL.SELECT_ALL"
                            ) as string
                        }{" "}
                        ({visibleEmails.length})
                    </Link>
                    <Text className={styles.folderLabel}>{folderName}</Text>
                </div>

                {/* Email List Section */}
                <div id="fluent9ScrollableEmailList" className={styles.emailListContainer}>
                    <EmailListFluent9
                        emails={visibleEmails}
                        selectedEmailIds={selectedEmailIds}
                        pagingItemCount={emailPagingItemCount}
                        onEmailSelectionChanged={handleEmailSelectionChanged}
                        isLoading={isLoadingEmails}
                        isLoadingMore={isLoadingMoreEmails}
                        isReloading={isReloadingEmails}
                        hasMore={emailPagingItemCount > 0 && !!emailOffsetToken}
                        onLoadMore={handleLoadMoreEmails}
                        scrollableTargetId="fluent9ScrollableEmailList"
                        disabled={isFiling}
                        searchFilter={searchTerm}
                    />
                </div>

                {/* Footer Section */}
                <div className={styles.footer}>
                    {isFiling && filingProgressTotal > 0 ? (
                        <div className={`${styles.submitProgress} ${isDarkMode ? "" : styles.submitProgressLight}`}>
                            <Info20Filled
                                aria-hidden="true"
                                style={{
                                    color: tokens.colorNeutralForeground3,
                                    width: "20px",
                                    height: "20px",
                                    flexShrink: 0,
                                }}
                            />
                            <div className={styles.submitProgressContent}>
                                <Text className={styles.submitProgressText}>{filingProgressMessage}</Text>
                                <ProgressBar
                                    className={styles.submitProgressBar}
                                    thickness="large"
                                    value={filingProgressValue}
                                    max={1}
                                />
                            </div>
                        </div>
                    ) : !isSubmitPromptHidden ? (
                        <Text className={styles.submitValidationLabel}>
                            <span>
                                {props.translate("FILE_MULTIPLE_EMAIL.SELECT_EMAIL_TO_FILE") as string}{" "}
                                <span className={styles.submitValidationAsterisk}>*</span>
                            </span>
                        </Text>
                    ) : null}
                    <div className={styles.submitButtonContainer}>
                        <Button
                            className={mergeClasses(styles.submitButton, isDarkMode && styles.submitButtonDark)}
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={isSubmitDisabled}
                            style={{ outline: "none", boxShadow: "none", border: "none" }}
                        >
                            {props.translate("FILE_MULTIPLE_EMAIL.SUBMIT_BUTTON")} ({selectedEmailsCount})
                        </Button>
                    </div>
                </div>
            </div>
        </FluentProvider>
    );
};

export default withLocalize(FileMultipleEmailFluent9Inner);
